import React from "react";
import clsx from "clsx";
import { useRouter } from "next/router";
import styles from "./STJobApplication.module.css";
import { Icons } from "@/const/icons";
import { STInput } from "../STInput";
import { STRadioGroup } from "../STRadioGroup";
import { STTextarea } from "../STTextarea";
import { STFileUpload } from "../STFileUpload";
import { STButton } from "../STButton";
import { STCareerHero } from "../STCareerHero";
import { useJobs } from "@/hooks";
import Link from "next/link";

export const STJobApplication = ({ job }) => {
  const router = useRouter();
  const {
    submitting,
    questions,
    formData,
    files,
    errors,
    handleChange,
    handleFileChange,
    handleSubmit,
    setFormData,
  } = useJobs(job);

  if (!job) return null;

  const onFormSubmit = (e) => {
    handleSubmit(e, job, () => {
      router.push(`/career/${job.slug}`);
    });
  };

  return (
    <>
      <STCareerHero />
      <section className="space-xl-py">
        <div className="container">
          <Link
            href={`/career/${job.slug}`}
            className={clsx(
              styles.backBtn,
              "d-inline-flex align-items-center gap-2 space-sm-mb",
            )}
          >
            {Icons.arrowLeft}
            <span>Go back</span>
          </Link>
          <div
            className={clsx(
              styles.layout,
              "d-flex flex-column flex-xl-row gap-sm align-items-start",
            )}
          >
            {/* Left Sidebar */}
            <div
              className={clsx(
                styles.jobInfoCard,
                "w-100 bg-off-white space-sm-p radius-md",
              )}
            >
              <h4 className="space-sm-mb big fw-normal lh-base">{job.title}</h4>
              <div className="d-flex flex-column gap-3">
                <div className="d-flex align-items-center gap-2 text-clr-gray mb-0">
                  {Icons.pinIcon}
                  <h6 className="mb-0 fw-normal">{job.location}</h6>
                </div>
                <div className="d-flex align-items-center gap-2 text-clr-gray">
                  {Icons.bagIcon}
                  <h6 className="mb-0 fw-normal">{job.type}</h6>
                </div>
              </div>
            </div>

            {/* Right Content / Form */}
            <div
              className={clsx(
                styles.formContent,
                "w-100 bg-off-white space-sm-p radius-md",
              )}
            >
              <form
                className={clsx(styles.formWrapper, "d-flex flex-column gap-sm")}
                onSubmit={onFormSubmit}
                noValidate
              >
                {(() => {
                  const activeQuestions = questions.filter(q => q.status !== false);
                  const rows = [];
                  let currentRow = [];

                  activeQuestions.forEach((q) => {
                    const isFullWidth = ["textarea", "editor", "file"].includes(q.type) || q.title.length > 50;
                    
                    if (isFullWidth) {
                      if (currentRow.length > 0) {
                        rows.push([...currentRow]);
                        currentRow = [];
                      }
                      rows.push([q]);
                    } else {
                      currentRow.push(q);
                      if (currentRow.length === 2) {
                        rows.push([...currentRow]);
                        currentRow = [];
                      }
                    }
                  });

                  if (currentRow.length > 0) {
                    rows.push(currentRow);
                  }

                  return rows.map((rowFields, rowIndex) => (
                    <div className={styles.row} key={rowIndex}>
                      {rowFields.map((q, colIndex) => {
                        if (q.type === "input") {
                          return (
                            <STInput
                              key={colIndex}
                              label={q.title}
                              placeholder={q.placeholder || q.title}
                              variant="bordered"
                              name={q.title}
                              value={formData[q.title] || ""}
                              handleChange={handleChange}
                              error={errors[q.title]}
                              prefix={q.title.toLowerCase().includes("name") ? Icons.cursorIcon : (q.title.toLowerCase().includes("email") ? Icons.atTheRateIcon : Icons.pinIcon)}
                            />
                          );
                        }
                        if (q.type === "textarea" || q.type === "editor") {
                          return (
                            <STTextarea
                              key={colIndex}
                              label={q.title}
                              placeholder={q.placeholder || "Write your answer here..."}
                              variant="bordered"
                              name={q.title}
                              value={formData[q.title] || ""}
                              handleChange={handleChange}
                              error={errors[q.title]}
                              rows={4}
                            />
                          );
                        }
                        if (q.type === "radio" || q.type === "select") {
                          return (
                            <STRadioGroup
                              key={colIndex}
                              label={q.title}
                              name={q.title}
                              value={formData[q.title] || ""}
                              onChange={(val) => setFormData(prev => ({ ...prev, [q.title]: val }))}
                              error={errors[q.title]}
                              optionClass={q.options?.length > 2 ? "flex-grow-1 flex-sm-grow-0" : "flex-grow-1"}
                              options={q.options || []}
                            />
                          );
                        }
                        if (q.type === "file") {
                          return (
                            <STFileUpload
                              key={colIndex}
                              label={q.title}
                              name={q.title}
                              onChange={handleFileChange(q.title)}
                              error={errors[q.title]}
                              className={styles.jobFileUploader}
                              buttonClass={styles.jobFileUploadButton}
                            />
                          );
                        }
                        return null;
                      })}
                    </div>
                  ));
                })()}

                <div className={styles.actions}>
                  <STButton
                    type="button"
                    variant="white"
                    disabled={submitting}
                    onClick={() => router.push(`/career/${job.slug}`)}
                    className={styles.cancelBtn}
                  >
                    Cancel
                  </STButton>
                  <STButton type="submit" loading={submitting}>
                    Submit
                  </STButton>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default STJobApplication;
